import { DeleteIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { JobMockFormData } from "../utils/job_types";
import { useJobManagementManager } from "../controllers/JobManagementController";
import { Job, JobSkill } from "../model/JobModel";
import { setOpenMenu } from "../viewmodel/job.action";
import { getJobApplicationDetails } from "../api/job_api";
import { JobStatus } from "../model/jobEnums";
import apiService from "../../../../services/api";

interface JobCardProps {
  job: Job;
  onEdit: (job: Job) => void;
  openMenuJobId: string | null;
  dispatch: React.Dispatch<any>;
  onDelete: (id: string) => void;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onClose: (id: string) => void;
  onCreateFromDraft?: (job: Job) => void;
}

export default function JobListCard({
  job,
  onEdit,
  openMenuJobId,
  dispatch,
  viewMode,
  onDelete,
  onPause,
  onResume,
  onClose,
  onCreateFromDraft,
}: JobCardProps & { viewMode?: string }) {
  // const [openMenu, setOpenMenu] = useState(false);
  const navigate = useNavigate();

  const { getStatusColor } = useJobManagementManager();
  const getCandidateIdForTracking = (job: Job): string => {
    const jobWithCandidate = job as any;
    return (
      jobWithCandidate.candidate_id ||
      jobWithCandidate.candidateId ||
      jobWithCandidate.candidate?.id ||
      jobWithCandidate.job_seeker_id ||
      jobWithCandidate.jobSeekerId ||
      ""
    );
  };

  const handleViewJobDetails = async (job: Job) => {
    const candidateId = getCandidateIdForTracking(job);

    if (candidateId) {
      try {
        await apiService.trackCandidateProfileView(candidateId);
      } catch (error) {
        console.warn("Profile view tracking failed:", error);
      }
    }

    navigate(`/company/JobDetails/${job.id}`, { state: job });
  };

  const statusStyles: Record<string, string> = {
    Active: "bg-green-100 text-green-700",
    Draft: "bg-gray-100 text-gray-700",
    Closed: "bg-red-100 text-red-700",
    published: "bg-yellow-100 text-yellow-700",
  };

  const isActionDisabled =
    job.status?.toLowerCase() === "rejected" ||
    job.status?.toLowerCase() === "under_review" ||
    job.status?.toLowerCase() === "under review" ||
    job.status?.toLowerCase() === "closed";

  const isEditDeleteDisabled = [JobStatus.Published, JobStatus.Paused].includes(
    job.status as JobStatus,
  );
  const pauseAction = job.status === JobStatus.Published;
  const resumeAction = job.status === JobStatus.Paused;
  const closeAction = [
    JobStatus.Published,
    JobStatus.Paused,
    JobStatus.Approved,
  ].includes(job.status as JobStatus);
  const isOpen = openMenuJobId === job.id;
  console.log("STATUS:", job.status);
  console.log("IS REJECTED:", job.status === JobStatus.Rejected);
  return (
    <div className="bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="grid grid-cols-[40px_1fr_auto] items-center gap-3 mb-3">
        {/* Avatar */}
        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
          <span className="text-indigo-600 font-medium text-sm">
            {job.title?.[0]?.toUpperCase()}
          </span>
        </div>

        {/* Title + Department */}
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-gray-900 truncate">
            {job.title}
          </h3>

          <p className="text-xs text-gray-500 truncate">
            {job.department || "No department"}
          </p>
        </div>

        {/* 3 dots menu (fixed position) */}
        {!isActionDisabled && (
          <div className="relative">
            <button
              onClick={() =>
                dispatch(setOpenMenu(openMenuJobId === job.id ? null : job.id))
              }
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              ⋮
            </button>

            {isOpen && (
              <div className="absolute right-0 top-8 w-32 bg-white border rounded-lg shadow-lg py-1 z-20">
                {!isEditDeleteDisabled ? (
                  <>
                    <button
                      onClick={() => {
                        dispatch(setOpenMenu(null));
                        onEdit(job);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    >
                      Edit
                    </button>

                    {job.status === JobStatus.Draft && onCreateFromDraft && (
                      <button
                        onClick={() => {
                          dispatch(setOpenMenu(null));
                          onCreateFromDraft(job);
                        }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 text-indigo-600"
                      >
                        Create Job
                      </button>
                    )}

                    <button
                      onClick={() => onDelete(job.id)}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 text-red-600"
                    >
                      Delete
                    </button>
                  </>
                ) : null}
                {pauseAction && (
                  <button
                    onClick={() => {
                      dispatch(setOpenMenu(null));
                      onPause(job.id);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Pause
                  </button>
                )}
                {resumeAction && (
                  <button
                    onClick={() => {
                      dispatch(setOpenMenu(null));
                      onResume(job.id);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Resume
                  </button>
                )}
                {closeAction && (
                  <button
                    onClick={() => {
                      dispatch(setOpenMenu(null));
                      onClose(job.id);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 text-red-600"
                  >
                    Close
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          {job.location && (
            <p className="text-xs text-gray-600 truncate">🌍 {job.location}</p>
          )}
        </div>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-normal bg-blue-100 text-blue-800 ${
            statusStyles[job.status] || "bg-blue-100 text-blue-700"
          }`}
        >
          {job.status}
        </span>
      </div>

      <div className="flex items-center space-x-2 justify-between">
        <span className="text-xs font-medium text-gray-700 mt-1">
          {job.salary_currency} {job.salary_min} - {job.salary_max}
        </span>
        <button
          onClick={() => handleViewJobDetails(job)}
          className="w-[10vh] text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700"
        >
          View
        </button>
      </div>
    </div>
    // <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200  h-full flex flex-col">
    //   {/* Header */}
    //   <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200 p-4 relative">
    //     <div className="grid grid-cols-3 gap-x-6 gap-y-4 items-center">
    //       <div className="text-sm font-medium text-gray-900 truncate">
    //         {job.title}
    //       </div>

    //       <div className="text-sm text-gray-700 truncate">
    //         {job.location || "-"}
    //       </div>

    //       <div className="flex justify-end items-center gap-2">
    //         <span
    //           className={`px-3 py-1 text-xs rounded-full font-medium ${
    //             statusStyles[job.status] || "bg-blue-100 text-blue-700"
    //           }`}
    //         >
    //           {job.status}
    //         </span>

    //         <div className="relative">
    //           <button
    //             onClick={() =>
    //               dispatch(
    //                 setOpenMenu(openMenuJobId === job.id ? null : job.id),
    //               )
    //             }
    //             className="p-2 hover:bg-gray-100 rounded-full"
    //           >
    //             ⋮
    //           </button>

    //           {isOpen && (
    //             <div className="absolute right-0 mt-2 w-32 bg-white border rounded-lg shadow-lg py-1 z-30">
    //               <button
    //                 onClick={() => {
    //                   dispatch(setOpenMenu(null));
    //                   onEdit(job);
    //                 }}
    //                 className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
    //               >
    //                 Edit
    //               </button>
    //               <button className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 text-red-600">
    //                 Delete
    //               </button>
    //             </div>
    //           )}
    //         </div>
    //       </div>

    //       <div className="text-sm text-blue-600 font-medium truncate">
    //         {job.department}
    //       </div>

    //       <div className="text-sm font-semibold text-gray-800">
    //         INR {job.salary_min} - {job.salary_max}
    //       </div>

    //       <div className="flex justify-end">
    //         <button
    //           onClick={() => {
    //             handleViewJobDetails(job);
    //           }}
    //           className=" text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 w-[10vh]"
    //         >
    //           View
    //         </button>
    //       </div>
    //     </div>
    //   </div>
    // </div>
  );
}
